import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";

type Language = "en" | "fr";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Header
    "nav.services": "Services",
    "nav.benefits": "Benefits",
    "nav.blog": "Blog",
    "nav.contact": "Contact",
    "nav.letsTalk": "Let's Talk",
    
    // Hero
    "hero.badge": "Fractional Marketing Leadership",
    "hero.title": "Your Part-Time CMO for",
    "hero.titleHighlight": "Full-Time Results",
    "hero.subtitle": "Strategic marketing leadership without the full-time commitment. Get Fortune 500 expertise at a fraction of the cost, exactly when you need it.",
    "hero.cta.primary": "Schedule a Consultation",
    "hero.cta.secondary": "See How It Works",
    "hero.trust": "Trusted by growing companies",
    
    // Problem
    "problem.badge": "The Challenge",
    "problem.title": "Growing Companies Face a Critical Gap",
    "problem.subtitle": "You need strategic marketing leadership, but a full-time CMO might not be the right fit.",
    "problem.1.title": "Budget Constraints",
    "problem.1.desc": "Full-time CMO salaries average $300K+ annually, plus equity and benefits.",
    "problem.2.title": "Inconsistent Strategy",
    "problem.2.desc": "Marketing teams without leadership often chase tactics instead of strategy.",
    "problem.3.title": "Scaling Challenges",
    "problem.3.desc": "What worked at $1M doesn't work at $10M. You need experienced guidance.",
    "problem.4.title": "Transition Periods",
    "problem.4.desc": "Between CMO hires or during pivots, leadership gaps hurt momentum.",
    
    // Services
    "services.badge": "What You Get",
    "services.title": "Part-Time CMO Services",
    "services.subtitle": "Strategic leadership tailored to your stage, goals, and resources.",
    "services.1.title": "Strategic Planning",
    "services.1.desc": "Go-to-market strategy, positioning, and messaging that differentiates you in the market.",
    "services.2.title": "Team Leadership",
    "services.2.desc": "Mentoring and managing your marketing team to execute at the highest level.",
    "services.3.title": "Performance Marketing",
    "services.3.desc": "Data-driven campaigns across paid, organic, and owned channels.",
    "services.4.title": "Brand Development",
    "services.4.desc": "Building a brand that resonates with your target audience and stands out.",
    "services.5.title": "Marketing Operations",
    "services.5.desc": "Tech stack optimization, process improvement, and scalable systems.",
    "services.6.title": "Board & Investor Relations",
    "services.6.desc": "Clear reporting and communication that demonstrates marketing ROI.",
    
    // Benefits
    "benefits.badge": "Why Fractional",
    "benefits.title": "The Power of Fractional Leadership",
    "benefits.subtitle": "Get C-suite expertise without the C-suite overhead. Here's why fractional makes sense.",
    "benefits.stat1.value": "60%",
    "benefits.stat1.label": "Cost Savings",
    "benefits.stat2.value": "2 Weeks",
    "benefits.stat2.label": "To Start",
    "benefits.stat3.value": "15+ Years",
    "benefits.stat3.label": "Experience",
    "benefits.1.title": "Executive Experience, Flexible Commitment",
    "benefits.1.desc": "Access senior marketing leadership for 10-20 hours per week, scaled to your needs.",
    "benefits.2.title": "Fresh Perspective, Proven Playbooks",
    "benefits.2.desc": "Benefit from cross-industry insights and strategies that have driven growth at multiple companies.",
    "benefits.3.title": "Speed to Impact",
    "benefits.3.desc": "Skip the lengthy hiring process. Start seeing strategic improvements within weeks, not months.",
    "benefits.4.title": "Bridge or Long-Term",
    "benefits.4.desc": "Whether you need interim leadership or ongoing strategic support, the engagement adapts to you.",
    
    // CTA
    "cta.badge": "Ready to Start?",
    "cta.title": "Let's Build Your",
    "cta.titleHighlight": "Marketing Engine",
    "cta.subtitle": "Fill out the form below and we'll get back to you within 24 hours to discuss your challenges, goals, and whether a part-time CMO is the right fit.",
    "cta.feature1": "Free consultation",
    "cta.feature2": "Custom growth plan",
    "cta.feature3": "No obligation",
    
    // Form
    "form.name": "Name",
    "form.name.placeholder": "John Doe",
    "form.email": "Email",
    "form.email.placeholder": "john@company.com",
    "form.company": "Company (optional)",
    "form.company.placeholder": "Your Company",
    "form.message": "Message",
    "form.message.placeholder": "Tell us about your marketing challenges and goals...",
    "form.submit": "Send Message",
    "form.submitting": "Sending...",
    "form.success.title": "Message sent!",
    "form.success.description": "We'll get back to you within 24 hours.",
    "form.error.title": "Error",
    "form.error.description": "Something went wrong. Please try again.",
    
    // Footer
    "footer.privacy": "Privacy",
    "footer.terms": "Terms",
    "footer.contact": "Contact",
    "footer.copyright": "Strategic marketing leadership for growing companies.",
    
    // Blog
    "blog.badge": "Insights & Resources",
    "blog.title": "Marketing Leadership Blog",
    "blog.subtitle": "Strategic insights from experienced fractional CMOs to help you grow your business.",
    "blog.notFound": "Article not found",
    "blog.backToList": "Back to articles",
    "blog.cta.title": "Ready to accelerate your growth?",
    "blog.cta.subtitle": "Let's discuss how a fractional CMO can help your business.",
    "blog.cta.button": "Schedule a Consultation",
  },
  fr: {
    // SEO Meta
    "meta.title": "CMO Externalisé Bordeaux et partout en France | Directeur Marketing à Temps Partiel France",
    "meta.description": "Directeur marketing externalisé à Bordeaux et partout en France. CMO fractionnel pour PME, startups et ETI en France et Nouvelle-Aquitaine. Expertise stratégie marketing, croissance et transformation digitale. Consultation gratuite.",
    
    // Header
    "nav.services": "Services",
    "nav.benefits": "Avantages",
    "nav.blog": "Blog",
    "nav.contact": "Contact",
    "nav.letsTalk": "Parlons-en",
    
    // Hero
    "hero.badge": "CMO Externalisé Bordeaux et partout en France",
    "hero.title": "Votre Directeur Marketing à Temps Partiel à",
    "hero.titleHighlight": "Bordeaux et partout en France",
    "hero.subtitle": "Expertise de directeur marketing pour les entreprises bordelaises et françaises. Un CMO externalisé senior pour piloter votre stratégie marketing, développer votre croissance et optimiser votre ROI — sans les coûts d'un CDI.",
    "hero.cta.primary": "Consultation Gratuite",
    "hero.cta.secondary": "Découvrir Nos Services",
    "hero.trust": "Accompagnement des entreprises à Bordeaux et partout en France",
    
    // Problem
    "problem.badge": "Les Défis des PME & ETI",
    "problem.title": "Pourquoi les Entreprises à Bordeaux et partout en France Choisissent un CMO Externalisé",
    "problem.subtitle": "Vous avez besoin d'un directeur marketing expérimenté, mais recruter un CMO en CDI représente un investissement considérable pour une PME ou startup.",
    "problem.1.title": "Budget Maîtrisé",
    "problem.1.desc": "Un directeur marketing en CDI coûte 80 à 150 k€ par an en coût total employeur. Notre solution de CMO externalisé divise ce coût par 3 à 5.",
    "problem.2.title": "Stratégie Marketing Cohérente",
    "problem.2.desc": "Sans direction marketing expérimentée, les équipes dispersent leurs efforts entre tactiques sans vision stratégique globale.",
    "problem.3.title": "Accompagnement à la Croissance",
    "problem.3.desc": "De startup à ETI, chaque étape de croissance nécessite une stratégie marketing adaptée. Un CMO fractionnel apporte cette expertise.",
    "problem.4.title": "Flexibilité et Réactivité",
    "problem.4.desc": "Entre deux recrutements ou lors d'une levée de fonds, un directeur marketing externalisé assure la continuité stratégique de votre entreprise.",
    
    // Services
    "services.badge": "Services CMO Bordeaux et partout en France",
    "services.title": "Directeur Marketing Externalisé : Nos Expertises",
    "services.subtitle": "Un accompagnement stratégique sur-mesure pour les entreprises de Bordeaux et partout en France et toute la France.",
    "services.1.title": "Stratégie Marketing & Go-to-Market",
    "services.1.desc": "Définition du positionnement, stratégie de lancement et plan marketing annuel pour les entreprises en croissance.",
    "services.2.title": "Management d'Équipe Marketing",
    "services.2.desc": "Encadrement et montée en compétences de vos équipes marketing. Recrutement et structuration du pôle marketing.",
    "services.3.title": "Acquisition & Marketing Digital",
    "services.3.desc": "Stratégie d'acquisition multicanale : SEO, SEA, réseaux sociaux, content marketing. Optimisation du tunnel de conversion.",
    "services.4.title": "Branding & Identité de Marque",
    "services.4.desc": "Construction d'une marque forte qui résonne avec votre marché en France et à l'international.",
    "services.5.title": "Marketing Ops & Automation",
    "services.5.desc": "Mise en place des outils marketing, CRM, automation et tableaux de bord pour piloter la performance.",
    "services.6.title": "Relations Investisseurs & Board",
    "services.6.desc": "Préparation des reportings marketing pour vos investisseurs et accompagnement lors des levées de fonds.",
    
    // Benefits
    "benefits.badge": "Pourquoi Choisir un CMO Fractionnel",
    "benefits.title": "L'Expertise d'un Directeur Marketing Senior",
    "benefits.subtitle": "Accédez à un CMO expérimenté pour votre entreprise, avec la flexibilité d'un accompagnement adapté à vos besoins.",
    "benefits.stat1.value": "60%",
    "benefits.stat1.label": "D'Économies vs CDI",
    "benefits.stat2.value": "2 Semaines",
    "benefits.stat2.label": "Délai de Démarrage",
    "benefits.stat3.value": "15+ Ans",
    "benefits.stat3.label": "Expérience Marketing",
    "benefits.1.title": "Expertise Senior, Engagement Flexible",
    "benefits.1.desc": "Un directeur marketing expérimenté disponible 2 à 12 jours par mois pour votre entreprise. Engagement modulable selon vos besoins.",
    "benefits.2.title": "Vision Multi-Sectorielle",
    "benefits.2.desc": "Fort de missions dans différents secteurs à Bordeaux et partout en France et au national, votre CMO externalisé apporte des best practices éprouvées.",
    "benefits.3.title": "Résultats Rapides",
    "benefits.3.desc": "Pas de période d'essai de 6 mois. Votre directrice marketing externalisée livre de premiers résultats dès les premières semaines.",
    "benefits.4.title": "Mission Intérim ou Long Terme",
    "benefits.4.desc": "Que ce soit pour 3 mois ou 2 ans, l'accompagnement CMO s'adapte à votre projet et à la trajectoire de votre entreprise.",
    
    // CTA
    "cta.badge": "Contactez Votre CMO",
    "cta.title": "Développons Ensemble Votre",
    "cta.titleHighlight": "Stratégie Marketing",
    "cta.subtitle": "Prenez rendez-vous pour un premier échange gratuit. Nous analyserons vos enjeux marketing et définirons comment un directeur marketing externalisé peut accélérer la croissance de votre entreprise.",
    "cta.feature1": "Échange gratuit 30 min",
    "cta.feature2": "Diagnostic marketing offert",
    "cta.feature3": "Sans engagement",
    
    // Form
    "form.name": "Nom",
    "form.name.placeholder": "Jean Dupont",
    "form.email": "Email",
    "form.email.placeholder": "jean@entreprise.com",
    "form.company": "Entreprise (optionnel)",
    "form.company.placeholder": "Votre Entreprise",
    "form.message": "Message",
    "form.message.placeholder": "Décrivez vos enjeux marketing et vos objectifs de croissance...",
    "form.submit": "Demander un Rendez-vous",
    "form.submitting": "Envoi...",
    "form.success.title": "Demande envoyée !",
    "form.success.description": "Nous vous recontactons sous 24h pour planifier notre échange.",
    "form.error.title": "Erreur",
    "form.error.description": "Une erreur s'est produite. Veuillez réessayer.",
    
    // Footer
    "footer.privacy": "Confidentialité",
    "footer.terms": "Mentions Légales",
    "footer.contact": "Contact",
    "footer.copyright": "CMO externalisé basé à Bordeaux. Directeur marketing à temps partiel pour PME, startups et ETI en France.",
    
    // Blog
    "blog.badge": "Ressources & Conseils",
    "blog.title": "Blog CMO Externalisé",
    "blog.subtitle": "Conseils stratégiques et insights de directeurs marketing expérimentés pour développer votre entreprise.",
    "blog.notFound": "Article non trouvé",
    "blog.backToList": "Retour aux articles",
    "blog.cta.title": "Prêt à accélérer votre croissance ?",
    "blog.cta.subtitle": "Discutons de comment un CMO externalisé peut aider votre entreprise.",
    "blog.cta.button": "Demander un Rendez-vous",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  // Default to "fr" on the server and for the first client render (hydration-safe);
  // the saved preference is applied right after mount.
  const [language, setLanguageState] = useState<Language>("fr");

  useEffect(() => {
    const savedLanguage = window.localStorage.getItem("cmo-partner-language");
    if (savedLanguage === "en" || savedLanguage === "fr") {
      setLanguageState(savedLanguage);
    }
  }, []);

  const setLanguage = (nextLanguage: Language) => {
    setLanguageState(nextLanguage);
    window.localStorage.setItem("cmo-partner-language", nextLanguage);
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
